import axios from "axios";
import { toast } from "sonner";
import { isOnline, putCache, getCache, enqueue, enqueueFile, labelFor } from "@/lib/offline";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Çevrimdışıyken: GET → önbellekten servis et, yazma işlemleri → kuyruğa al
async function offlineFallback(config, method, isGet) {
  if (isGet) {
    const cached = await getCache(config);
    if (cached !== undefined) {
      return { data: cached, status: 200, statusText: "OK (offline-cache)", headers: {}, config, request: {}, __fromCache: true };
    }
    const e = new Error("Çevrimdışısınız ve bu veri henüz önbelleğe alınmamış.");
    e.config = config; e.__offline = true;
    throw e;
  }
  const url = config.url || "";
  // Kimlik doğrulama işlemleri çevrimdışı kuyruğa alınamaz (parola doğrulanamaz)
  if (url.startsWith("/auth/")) {
    const e = new Error("Bu işlem için internet bağlantısı gereklidir.");
    e.__offline = true; throw e;
  }
  // Dosya/multipart yüklemeleri (FormData) adapter üzerinden kuyruğa alınamaz — özel akışla (enqueueMedia) yönetilir
  if (typeof FormData !== "undefined" && config.data instanceof FormData) {
    const e = new Error("Çevrimdışı — dosya yüklemesi daha sonra tekrar denenecek.");
    e.__offline = true; throw e;
  }
  let data = config.data;
  if (typeof data === "string") { try { data = JSON.parse(data); } catch {} }
  const tempId = `offline_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
  await enqueue({ kind: "mutation", method, url: config.url, data, tempId, label: labelFor(method, config.url, data) });
  const optimistic = { id: tempId, _offline: true, ...(data && typeof data === "object" ? data : {}) };
  return { data: optimistic, status: 200, statusText: "OK (queued)", headers: {}, config, request: {}, __queued: true };
}

async function offlineAdapter(config) {
  const method = (config.method || "get").toLowerCase();
  const isGet = method === "get";
  const defAdapter = axios.getAdapter(axios.defaults.adapter);
  if (isOnline()) {
    try {
      const res = await defAdapter(config);
      if (isGet) putCache(config, res.data);
      return res;
    } catch (err) {
      if (err.response) throw err; // gerçek HTTP hatası (4xx/5xx)
      return offlineFallback(config, method, isGet); // ağ hatası → offline davran
    }
  }
  return offlineFallback(config, method, isGet);
}

export const api = axios.create({ baseURL: API, adapter: offlineAdapter });

api.interceptors.request.use((config) => {
  const t = localStorage.getItem("token");
  if (t) config.headers.Authorization = `Bearer ${t}`;
  return config;
});

api.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err.response?.status === 401 && localStorage.getItem("token")) {
      localStorage.removeItem("token");
      window.location.reload();
    }
    return Promise.reject(err);
  }
);

// Sunucuda üretilen dosyaları (PDF/Excel) aç — çevrimdışıysa kuyruğa al, internet gelince indirilir
// Dönüş: "opened" (yeni sekme açıldı) veya "queued" (çevrimdışı kuyruğa alındı)
export async function openServerFile(url, label) {
  if (isOnline()) { window.open(url, "_blank"); return "opened"; }
  await enqueueFile({ url, label });
  toast.info("Çevrimdışısınız — belge kuyruğa alındı, internet gelince hazırlanacak.", { duration: 4000 });
  return "queued";
}

export const EGG_ORDER = ["jumbo_30", "jumbo", "super", "duble", "eski_ana", "yeni_ana", "yarka", "pilic", "klavuz", "cift_sari_20", "cift_sari_30"];
export const DEFECT_ORDER = ["kirli", "kirik", "damar_catlagi", "ayirma"];
export const MULTIPLIERS = {
  jumbo: 20, cift_sari_20: 20,
  jumbo_30: 30, eski_ana: 30,
  duble: 30, super: 30, yeni_ana: 30, yarka: 30, pilic: 30, klavuz: 30, cift_sari_30: 30,
  kirli: 30, kirik: 30, damar_catlagi: 30, ayirma: 30,
};
export const BREEDS = ["kirmizi", "tintin", "beyaz"];
export const BREED_LABELS = { kirmizi: "Kırmızı Tavuk", tintin: "Tintin Tavuk", beyaz: "Beyaz Tavuk" };

export const errMsg = (e, fallback = "Bir hata oluştu") => {
  const d = e?.response?.data?.detail;
  if (!d) return e?.message || fallback;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x.msg || JSON.stringify(x)).join(", ");
  if (typeof d === "object") return d.message || JSON.stringify(d);
  return fallback;
};

export const fmt = (n) => (n === null || n === undefined || isNaN(n) ? "0" : Number(n).toLocaleString("tr-TR"));
export const fmtDec = (n) => (n === null || n === undefined || isNaN(n) ? "0" : Number(n).toLocaleString("tr-TR", { maximumFractionDigits: 2 }));

// Sürü yaşı: referans tarih + üretim tarihinden hafta/gün hesaplar (timezone güvenli, UTC midnight)
export function calculateFlockAge(referenceDateStr, productionDateStr, initialWeek = 0) {
  if (!referenceDateStr || !productionDateStr) return { totalDays: 0, currentWeek: initialWeek, dayOfWeek: 0, formattedText: `${initialWeek}. Hafta` };
  const ref = new Date(referenceDateStr), prod = new Date(productionDateStr);
  const utcRef = Date.UTC(ref.getFullYear(), ref.getMonth(), ref.getDate());
  const utcProd = Date.UTC(prod.getFullYear(), prod.getMonth(), prod.getDate());
  const diffDays = Math.floor((utcProd - utcRef) / 86400000);
  if (diffDays < 0) return { totalDays: 0, currentWeek: initialWeek, dayOfWeek: 0, formattedText: "Geçersiz Tarih" };
  const totalDays = diffDays + 1;
  const currentWeek = initialWeek + Math.floor(diffDays / 7);
  const dayOfWeek = (diffDays % 7) + 1;
  return { totalDays, currentWeek, dayOfWeek, formattedText: `${currentWeek}. Hafta (${totalDays}. Gün)` };
}
